import React, { useState, useEffect } from 'react';
import { clientsApi } from '../services/api';
import './ClientsManagement.css';

const ClientsManagement = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [newClientName, setNewClientName] = useState('');

  // Load clients on component mount
  useEffect(() => {
    fetchClients();
  }, []);

  // Fetch clients from API
  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await clientsApi.getClients();
      setClients(response.clients || []);
      setError('');
    } catch (err) {
      setError('Failed to load clients. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle create client form submission
  const handleCreateClient = async (e) => {
    e.preventDefault();
    
    if (!newClientName.trim()) {
      setError('Client name cannot be empty');
      return;
    }
    
    try {
      setLoading(true);
      
      // Create client
      await clientsApi.createClient({ name: newClientName });
      
      // Reset form
      setNewClientName('');
      setShowNewClientForm(false);
      
      // Refresh client list
      await fetchClients();
      
      setError('');
    } catch (err) {
      setError('Failed to create client. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle delete client
  const handleDeleteClient = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client? This will delete all associated agents and data.')) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Delete client
      await clientsApi.deleteClient(clientId);
      
      // Refresh client list
      await fetchClients();
      
      setError('');
    } catch (err) {
      setError('Failed to delete client. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle regenerate API key
  const handleRegenerateApiKey = async (clientId) => {
    if (!window.confirm('Are you sure you want to regenerate this client\'s API key? This will invalidate the old key.')) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Regenerate API key
      await clientsApi.regenerateApiKey(clientId);
      
      // Refresh client list
      await fetchClients();
      
      setError('');
    } catch (err) {
      setError('Failed to regenerate API key. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="header">
        <h1>Clients Management</h1>
        <button 
          className="btn"
          onClick={() => setShowNewClientForm(true)}
        >
          Add New Client
        </button>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      {showNewClientForm && (
        <div className="card new-client-form">
          <h3>Create New Client</h3>
          <form onSubmit={handleCreateClient}>
            <div className="form-group">
              <label htmlFor="client-name" className="form-label">Client Name</label>
              <input
                type="text"
                id="client-name"
                value={newClientName}
                onChange={(e) => setNewClientName(e.target.value)}
                className="form-control"
                placeholder="Enter client name"
                required
              />
            </div>
            <div className="form-actions">
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => {
                  setShowNewClientForm(false);
                  setNewClientName('');
                }}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn"
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Client'}
              </button>
            </div>
          </form>
        </div>
      )}
      
      <div className="card">
        {loading && !showNewClientForm ? (
          <div className="loading">Loading clients...</div>
        ) : clients.length === 0 ? (
          <div className="empty-state">
            <p>No clients found.</p>
            <button 
              className="btn"
              onClick={() => setShowNewClientForm(true)}
            >
              Add Your First Client
            </button>
          </div>
        ) : (
          <div className="clients-list">
            <table className="table">
              <thead>
                <tr>
                  <th>Client Name</th>
                  <th>API Key</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {clients.map(client => (
                  <tr key={client.id}>
                    <td>{client.name}</td>
                    <td className="api-key-cell">
                      <span className="api-key">{client.api_key}</span>
                      <button
                        className="btn-copy"
                        onClick={() => {
                          navigator.clipboard.writeText(client.api_key);
                          alert('API key copied to clipboard');
                        }}
                        title="Copy API Key"
                      >
                        Copy
                      </button>
                    </td>
                    <td>{new Date(client.created_at).toLocaleDateString()}</td>
                    <td className="actions-cell">
                      <button
                        className="btn-action regenerate"
                        onClick={() => handleRegenerateApiKey(client.id)}
                        title="Regenerate API Key"
                      >
                        Regenerate Key
                      </button>
                      <button
                        className="btn-action delete"
                        onClick={() => handleDeleteClient(client.id)}
                        title="Delete Client"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientsManagement;
